import { useEffect, useId, useRef, useState } from 'react';
import { OFF } from '../lib/off';
import { ApiError, api, type GreetingChange, type GreetingView } from '../api/client';
import { browserVoice, canSaveGreeting, greetingChoices, greetingCounter, greetingOutcome, playingLabel, previewGreeting } from '../lib/greeting';
import { useData, type DataState } from '../lib/useData';
import { Button, Card, Chip, LoadError, Loading, Notice } from './ui';

/**
 * A voicemail greeting typed in Stitch (1.12.33, Teampace 2976): type the words, Listen for a rough
 * preview in the browser's own voice, Save to make it the greeting callers hear. Shows which plays
 * now (the standard one, a recorded one, or the typed one) and switches back to a recorded or the
 * standard one. People change their own (My phone); admins anyone's (a person's page, Voicemail
 * boxes). Each change is a change set: in Change history, with undo.
 */
export function VoicemailGreetingCard({ domain, extension, own, title }: { domain: string; extension?: string | undefined; own: boolean; title?: string }) {
  const data = useData(() => api.greeting(domain, extension), [domain, extension]);
  // A phone system (or mailbox) without greetings: nothing to show.
  if (data.error instanceof ApiError && data.error.code === 'NOT_AVAILABLE' && data.error.status === 404) return null;
  return (
    <Card title={title ?? (own ? 'Your voicemail greeting' : 'Voicemail greeting')} id="voicemail-greeting">
      <div className="card-body">
        <VoicemailGreetingPanel domain={domain} extension={extension} own={own} data={data} />
      </div>
    </Card>
  );
}

/** The panel with its own loading (a voicemail box's dialog). */
export function VoicemailGreetingLoaded({ domain, extension, own }: { domain: string; extension?: string | undefined; own: boolean }) {
  const data = useData(() => api.greeting(domain, extension), [domain, extension]);
  return <VoicemailGreetingPanel domain={domain} extension={extension} own={own} data={data} />;
}

/** The panel, from a read of the greeting. */
export function VoicemailGreetingPanel({ domain, extension, own, data }: { domain: string; extension?: string | undefined; own: boolean; data: DataState<GreetingView> }) {
  const [view, setView] = useState<GreetingView | undefined>();
  const [draft, setDraft] = useState<string | null>(null);
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState<{ tone: 'ok' | 'neutral' | 'crit'; text: string } | null>(null);
  const [speaking, setSpeaking] = useState(false);
  const stop = useRef<(() => void) | null>(null);
  const voice = useRef(browserVoice());

  // The latest read (a change elsewhere loads it again); the words typed so far stay.
  useEffect(() => {
    if (data.data) setView(data.data);
  }, [data.data]);
  useEffect(() => {
    if (draft === null && view) setDraft(view.typed?.text ?? '');
  }, [view, draft]);
  // Stop reading aloud when the panel goes.
  useEffect(() => () => stop.current?.(), []);

  if (!view) return data.error ? <LoadError error={data.error} retry={data.reload} /> : <Loading label="Loading the greeting" />;

  async function send(change: GreetingChange) {
    setBusy(change.use === 'typed' && change.text !== undefined ? 'save' : `use-${change.use === 'recorded' ? change.slot : change.use}`);
    setNotice(null);
    try {
      const r = await api.setGreeting(domain, change, extension);
      if (r.greeting) setView(r.greeting);
      else data.reload();
      setNotice(greetingOutcome(r, change, own));
    } catch (err) {
      setNotice({ tone: 'crit', text: err instanceof Error ? err.message : 'That didn’t work. Try again.' });
    } finally {
      setBusy('');
    }
  }

  function listen() {
    if (speaking) {
      stop.current?.();
      return;
    }
    const v = voice.current;
    if (!v || !draft?.trim()) return;
    setSpeaking(true);
    stop.current = previewGreeting(draft, view?.lang ?? 'en', v, () => {
      setSpeaking(false);
      stop.current = null;
    });
  }

  return (
    <GreetingPanelBody
      view={view}
      own={own}
      draft={draft ?? ''}
      onDraft={(t) => {
        setDraft(t);
        setNotice(null);
      }}
      canListen={Boolean(voice.current)}
      speaking={speaking}
      onListen={listen}
      onSave={() => draft !== null && void send({ use: 'typed', text: draft })}
      onUse={(c) => void send(c)}
      busy={busy}
      notice={notice}
    />
  );
}

/** What the panel shows, from its state (no requests): also what the web tests render. */
export function GreetingPanelBody({
  view,
  own,
  draft,
  onDraft,
  canListen,
  speaking,
  onListen,
  onSave,
  onUse,
  busy,
  notice
}: {
  view: GreetingView;
  own: boolean;
  draft: string;
  onDraft: (text: string) => void;
  canListen: boolean;
  speaking: boolean;
  onListen: () => void;
  onSave: () => void;
  onUse: (change: GreetingChange) => void;
  busy: string;
  notice: { tone: 'ok' | 'neutral' | 'crit'; text: string } | null;
}) {
  const id = useId();
  const count = greetingCounter(draft, view.maxLength);
  const save = canSaveGreeting(view, draft);
  const choices = greetingChoices(view, own).filter((c) => !c.playing);
  const whose = own ? 'your' : view.mailbox ? 'this box’s' : 'their';
  return (
    <div className="vm-greeting">
      <p className="vm-greeting-now" aria-live="polite">
        <span className="muted">Callers hear</span> <strong>{playingLabel(view.playing, own)}</strong>
        {view.playing.kind === 'typed' && (
          <Chip small tone="brand">
            Typed in Stitch
          </Chip>
        )}
      </p>
      {view.playing.kind === 'typed' && <blockquote className="vm-greeting-words">“{view.playing.text}”</blockquote>}

      {view.canChange ? (
        <>
          <div className="field">
            <label htmlFor={`${id}-words`}>{view.typed ? 'Change the typed greeting' : 'Type a greeting'}</label>
            <textarea
              id={`${id}-words`}
              className="input textarea"
              rows={3}
              value={draft}
              aria-invalid={count.over ? true : undefined}
              aria-describedby={`${id}-count ${id}-hint`}
              placeholder={
                view.mailbox
                  ? `You’ve reached ${view.name}. Leave your name, number and a short message, and we’ll call you back.`
                  : `Hi, you’ve reached ${view.name}. I can’t take your call right now. Leave a message and I’ll call you back.`
              }
              onChange={(e) => onDraft(e.target.value)}
            />
            <div className="vm-greeting-meta">
              <span className="hint" id={`${id}-hint`}>
                A few sentences. The phone system reads it out before the beep.
              </span>
              <span className={count.over ? 'error vm-greeting-count' : 'muted vm-greeting-count'} id={`${id}-count`} aria-live="polite">
                {count.label}
                <span className="sr-only">{count.over ? ` characters: ${-count.left} too many` : ' characters'}</span>
              </span>
            </div>
          </div>
          <div className="vm-greeting-actions">
            <Button icon={speaking ? 'stop' : 'volume'} disabled={!canListen || !draft.trim()} onClick={onListen} title={canListen ? undefined : 'This browser can’t read text aloud'}>
              {speaking ? 'Stop' : 'Listen'}
            </Button>
            <Button variant="primary" icon="check" disabled={!save.ok || Boolean(busy)} onClick={onSave} title={save.ok ? undefined : save.why}>
              {busy === 'save' ? 'Saving…' : 'Save'}
            </Button>
            {!save.ok && !save.same && draft.trim() !== '' && <span className="muted small">{save.why}</span>}
          </div>
          <p className="muted small vm-greeting-note">
            Listen uses your browser’s own voice, so it’s a rough preview: the phone system’s voice sounds a little different. Call {whose} voicemail to hear the real one. Save
            makes it the greeting callers hear.
          </p>
          {choices.length > 0 && (
            <div className="vm-greeting-switch" role="group" aria-labelledby={`${id}-instead`}>
              <span className="field-label" id={`${id}-instead`}>
                Or play
              </span>
              {choices.map((c) => (
                <Button key={c.key} size="small" variant="secondary" disabled={Boolean(busy)} onClick={() => onUse(c.change)}>
                  {busy === `use-${c.change.use === 'recorded' ? c.change.slot : c.change.use}` ? 'One moment…' : c.label}
                </Button>
              ))}
            </div>
          )}
          {!view.slotForTyped && !view.typed && (
            <Notice tone="warn">Every personal greeting holds a recording, and Stitch never records over one. Delete one from voicemail on the phone to type a greeting here.</Notice>
          )}
        </>
      ) : (
        <p className="muted small">{own ? 'Your company’s admin can change it.' : OFF.readOnly}</p>
      )}
      {notice && <Notice tone={notice.tone}>{notice.text}</Notice>}
    </div>
  );
}
